import { motion } from 'motion/react';
import { Helmet } from 'react-helmet-async';
import { Shield, Anchor, Package, Compass, ArrowRight, Play, Activity, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useBrand } from '../context/BrandContext';
import { useUser } from '../context/UserContext';

import { OptimizedImage } from './OptimizedImage';

export default function Anatomy() {
  const { assets, resolveMediaUrl, content } = useBrand();
  const { user: currentUser, profile, isMember, isAdmin, loading: userLoading } = useUser();

  const hasSyndicateMembership = (isMember || isAdmin || currentUser?.email?.toLowerCase() === 'tshark1975@gmail.com') && profile?.boardOrdered !== true;

  return (
    <section className="ows-product-section relative overflow-hidden" id="OWS EXPEDITION CX2">
      <Helmet>
        <title>OWS EXPEDITION CX2 | OWS Expedition SUP Engineering</title>
        <meta name="description" content="Explore the technical anatomy of the OWS EXPEDITION CX2. Displacement hull mastery, dual-chamber safety, and minimalist design for serious water exploration." />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto mb-20">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="flex items-center justify-center gap-3 text-ows-teal mb-6"
          >
            <Compass className="h-6 w-6" />
            <span className="text-xs font-bold tracking-[0.4em] uppercase font-mono">THE ART OF THE VESSEL</span>
          </motion.div>
          
          <div className="overflow-hidden mb-10">
            <motion.h2 
              initial={{ y: "100%" }}
              whileInView={{ y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="text-4xl md:text-6xl font-display font-light uppercase tracking-widest text-ows-dark mb-4"
            >
              THE OWS EXPEDITION CX2 <br />
              <span className="text-[#0D3A40] text-xs md:text-sm font-sans font-bold uppercase tracking-[0.18em] mt-4 block">beyond the shoreline. beyond the limit.</span>
            </motion.h2>
          </div>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
            className="text-[#2D3748] text-[15px] md:text-base font-light tracking-wide leading-relaxed max-w-[650px] mx-auto text-center"
          >
            The OWS EXPEDITION CX2 is more than gear. It's the mechanical foundation of our elite community. Engineered for the long-range expedition missions that define us.
          </motion.p>
        </div>
        
        <div className="space-y-12">
           {/* Video Section - Full Width */}
           <motion.div 
             initial={{ opacity: 0, y: 30 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             className="relative ows-unified-corners bg-ows-sand shadow-2xl aspect-video group w-full"
           >
             <video 
               src={resolveMediaUrl(assets.vesselVideo) || "https://joy1.videvo.net/videvo_files/video/free/2019-11/large_watermarked/190301_08_GOPRO_03_preview.mp4"} 
               autoPlay 
               loop 
               muted 
               playsInline
               className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
             />
             <div className="absolute inset-0 bg-gradient-to-t from-[#141414]/10 via-transparent to-transparent opacity-60" />
             <div className="absolute bottom-8 left-8 flex items-center gap-4 text-white">
                <div className="w-10 h-10 rounded-full bg-ows-teal flex items-center justify-center animate-pulse">
                   <Play className="h-4 w-4 fill-white text-white" />
                </div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  color: '#0D3A40',
                  padding: '6px 14px',
                  fontFamily: 'monospace',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }} className="rounded shadow-sm">
                  FLEET HARDWARE // OWS EXPEDITION CX2 DEPLOYMENT
                </div>
             </div>
           </motion.div>

           {/* Gallery Section - Below Video */}
           <div className="ows-grid-gap-control">
              {assets.vesselGallery && assets.vesselGallery.length > 0 ? (
                assets.vesselGallery.map((img, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex-[1] ows-unified-corners bg-ows-sand group"
                  >
                    <OptimizedImage 
                      src={resolveMediaUrl(img)} 
                      alt={`Vessel detail ${i + 1}`} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </motion.div>
                ))
              ) : (
                <>
                  <motion.div 
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    className="flex-[0_0_20%] ows-unified-corners bg-ows-sand group"
                  >
                    <OptimizedImage 
                      src={resolveMediaUrl(assets.boardImage)} 
                      alt="Vessel Side Profile" 
                      className="w-full h-full"
                      imgClassName="lower-grid-images transition-transform duration-1000 group-hover:scale-105"
                    />
                  </motion.div>
                  <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="flex-[0_0_40%] flex flex-col justify-center bg-[#F8FAFC] p-10 md:p-12 ows-unified-corners relative overflow-hidden group/card"
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#0D3A40]/5 rounded-full -mr-16 -mt-16 blur-2xl group-hover/card:bg-[#0D3A40]/10 transition-colors" />
                    
                    <div className="relative space-y-6 flex-grow">
                      <div className="inline-block px-4 py-1.5 bg-[#0D3A40]/5 text-[#0D3A40] text-[9px] uppercase tracking-[0.3em] font-bold font-mono rounded-full ring-1 ring-[#0D3A40]/10">
                        Technical Intel
                      </div>
                      <div className="space-y-6">
                        <h4 className="text-2xl font-serif uppercase tracking-widest text-[#0D3A40] leading-tight">Technical Briefing</h4>
                        <div className="space-y-4">
                          <p className="technical-bullet-points">
                            <strong className="text-[#0D3A40]">{"// DUAL-CHAMBER SYSTEM"}</strong> – Independently pressurized internal safety cells for absolute survival redundancy.
                          </p>
                          <p className="technical-bullet-points">
                            <strong className="text-[#0D3A40]">{"// NAVAL-WHITE HULL"}</strong> – High-visibility architecture engineered for immediate maritime awareness in busy harbors.
                          </p>
                          <p className="technical-bullet-points">
                            <strong className="text-[#0D3A40]">{"// DISPLACEMENT NOSE"}</strong> – Tailored volume distribution to smoothly cut through heavy tracking chop and boat wakes.
                          </p>
                          <p className="technical-bullet-points">
                            <strong className="text-[#0D3A40]">{"// UTILITY RIGGING"}</strong> – Seamless integrated D-ring matrix for secure, long-range expedition gear deployment.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8">
                      <Link 
                        to="/OWS EXPEDITION CX2"
                        className="group/btn relative inline-flex items-center justify-between w-full py-5 px-8 bg-[#0D3A40] text-white rounded-xl hover:bg-ows-teal transition-all active:scale-95"
                      >
                        <span className="text-[10px] font-bold uppercase tracking-[0.4em]">View Technical Overview</span>
                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center transition-all transform group-hover/btn:translate-x-1 group-hover/btn:bg-white group-hover/btn:text-ows-teal">
                          <ArrowRight className="h-4 w-4" />
                        </div>
                      </Link>

                      <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[#0D3A40]/10">
                        <span className="bg-[#0D3A40] text-[#E4E3E0] px-3 py-1 text-[8px] font-bold tracking-[0.3em] uppercase font-mono rounded">B01-Unit: ACTIVE</span>
                        {hasSyndicateMembership && (
                          <div className="flex items-center gap-2 px-2 py-1 bg-ows-teal/10 rounded-full border border-ows-teal/20">
                            <ShieldCheck className="h-2 w-2 text-ows-teal" />
                            <span className="text-[7px] font-bold uppercase tracking-widest text-ows-teal">Syndicate Clearance</span>
                          </div>
                        )}
                        {!hasSyndicateMembership && !userLoading && (
                          <div className="flex items-center gap-2">
                            <Activity className="h-2 w-2 text-ows-teal animate-pulse" />
                            <span className="text-[8px] font-mono font-bold uppercase tracking-widest text-[#0D3A40]/40">Industrial Specification Node</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                  <motion.div 
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    className="flex-[0_0_40%] ows-unified-corners bg-ows-sand group"
                  >
                    <OptimizedImage 
                      src={resolveMediaUrl(assets.detailImage)} 
                      alt="Vessel Engineering" 
                      className="w-full h-full"
                      imgClassName="lower-grid-images transition-transform duration-1000 group-hover:scale-105"
                    />
                  </motion.div>
                </>
              )}
           </div>
        </div>
      </div>
    </section>
  );
}
